/**
 * Axios API client with interceptors
 * Handles auth tokens, refresh, and error transformation
 */
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { STORAGE_KEYS, API_TIMEOUT } from '../constants';
import { storage } from '../platform/storage';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import type { ApiError } from '../types';

import { API_BASE_URL } from '../config/env';
const BASE_URL = API_BASE_URL;

import {
  ensureValidAccessToken,
  isAccessTokenExpired,
  refreshAccessToken,
  shouldAttemptTokenRefresh,
} from './auth/tokenRefresh';

/** Request config augmented with one-shot retry flags. */
interface RetriableRequestConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

type QueuedRequest = {
  resolve: (value: AxiosResponse) => void;
  reject: (reason?: unknown) => void;
  config: RetriableRequestConfig;
};

type FailedRequest = {
  code?: string;
  message?: string;
  response?: {
    status: number;
    data?: unknown;
  };
  config?: RetriableRequestConfig;
};

let isRefreshingAfter401 = false;
let refreshWaitQueue: QueuedRequest[] = [];

const flushRefreshQueue = (error: unknown | null, token: string | null): void => {
  refreshWaitQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(toUserFacingError(error));
      return;
    }
    if (!token) {
      reject(new Error('Session expired'));
      return;
    }
    config._retried = true;
    config.headers.Authorization = `Bearer ${token}`;
    apiClient(config).then(resolve, reject);
  });
  refreshWaitQueue = [];
};

const getBearerTokenFromConfig = (
  config?: InternalAxiosRequestConfig,
): string | null => {
  if (!config?.headers) {
    return null;
  }
  const headers = config.headers as {
    Authorization?: unknown;
    authorization?: unknown;
    get?: (name: string) => unknown;
  };
  const value =
    typeof headers.get === 'function'
      ? (headers.get('Authorization') ?? headers.get('authorization'))
      : (headers.Authorization ?? headers.authorization);
  if (typeof value !== 'string' || !value.startsWith('Bearer ')) {
    return null;
  }
  const token = value.slice(7).trim();
  return token || null;
};

const toUserFacingError = (error: unknown): Error => {
  const message = getApiErrorMessage(error as ApiError);
  if (error instanceof Error) {
    error.message = message;
    return error;
  }
  const wrapped = new Error(message);
  if (error && typeof error === 'object') {
    Object.assign(wrapped, error, { message });
  }
  return wrapped;
};

const isAuthStatus = (status: number | undefined): boolean =>
  status === 401;

/**
 * Recover a failed call when the access token is stale:
 * 401, or 5xx/timeout after idle with an expired JWT.
 * Concurrent callers share one refresh and retry afterward.
 */
const recoverOrReject = async (error: FailedRequest): Promise<AxiosResponse> => {
  const originalRequest = error.config;
  const status = error.response?.status;
  const canAttemptRefresh =
    Boolean(originalRequest) &&
    !originalRequest?._retried &&
    shouldAttemptTokenRefresh(originalRequest?.url);

  const bearerExpired = isAccessTokenExpired(getBearerTokenFromConfig(originalRequest));
  const shouldRefreshSession =
    canAttemptRefresh &&
    (isAuthStatus(status) ||
      ((status == null || status >= 500) && bearerExpired));

  if (shouldRefreshSession && originalRequest) {
    if (isRefreshingAfter401) {
      return new Promise<AxiosResponse>((resolve, reject) => {
        refreshWaitQueue.push({
          resolve,
          reject,
          config: originalRequest,
        });
      });
    }

    originalRequest._retried = true;
    isRefreshingAfter401 = true;

    try {
      const token = await refreshAccessToken();
      flushRefreshQueue(null, token);
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      flushRefreshQueue(refreshError, null);
      return Promise.reject(toUserFacingError(refreshError));
    } finally {
      isRefreshingAfter401 = false;
    }
  }

  return Promise.reject(toUserFacingError(error));
};

const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

apiClient.interceptors.request.use(
  async config => {
    // Let the runtime set multipart boundary — a hard-coded Content-Type
    // without boundary breaks FormData uploads (comments, posts, etc.).
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers && typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else if (config.headers) {
        delete (config.headers as Record<string, unknown>)['Content-Type'];
      }
    }

    if (!shouldAttemptTokenRefresh(config.url)) {
      const token = await storage.getItem<string>(STORAGE_KEYS.AUTH_TOKEN);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    }

    try {
      const token = await ensureValidAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      return Promise.reject(toUserFacingError(error));
    }

    return config;
  },
  error => Promise.reject(toUserFacingError(error)),
);

apiClient.interceptors.response.use(
  response => {
    const data = response.data as { success?: boolean; statusCode?: number; message?: string };
    if (data && data.success === false) {
      return recoverOrReject({
        response: {
          status: data.statusCode || response.status || 400,
          data,
        },
        message: data.message || 'API Error',
        config: response.config as RetriableRequestConfig,
      });
    }
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as RetriableRequestConfig & { _networkRetries?: number };
    
    // Automatically retry transient network errors (no response, timeout, etc.) for GET requests
    if (config && !error.response && error.isAxiosError && config.method?.toLowerCase() === 'get') {
      config._networkRetries = config._networkRetries || 0;
      if (config._networkRetries < 2) {
        config._networkRetries += 1;
        await new Promise(resolve => setTimeout(() => resolve(undefined), 1000));
        return apiClient(config);
      }
    }
    
    return recoverOrReject(error as FailedRequest);
  },
);

export default apiClient;
